import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import { useRef, useState, useCallback } from "react";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { useTranslation } from "@shared/utils/internationalization";
import { PurchaseWizardModalStep } from "@app-contexts/public/shared/checkout/components/PurchaseWizardModal/PurchaseWizardModalStep";
import { recordAppError, logAppInfo } from "@shared/utils/errors/errorLogger";
import { AnalyticsService } from "@shared/services/analytics";
import {
  ProductAddonsModalContent,
  VariantQuantityMap,
} from "./ProductAddonsModalContent";

const PRODUCT_STEP_ID = "product";

export interface ProductStepProps {
  product: ProductTypeSerializer;
  hideDescription?: boolean;
}

const hasValidCount = (items: VariantQuantityMap) => {
  return (
    Object.values(items).reduce((acc, { quantity }) => acc + quantity, 0) > 0
  );
};

export const addProductToBasket = async (
  items: VariantQuantityMap,
  publicBasket: ReturnType<typeof usePublicBasket>
) => {
  try {
    const returnedBasket = await publicBasket.modifyItems(
      Object.values(items).map(({ variant, quantity }) => ({
        item: variant,
        quantity,
      }))
    );

    AnalyticsService.cartUpdated(returnedBasket);

    return returnedBasket;
  } catch (error) {
    logAppInfo("Error changing product quantity");
    recordAppError(error);

    return undefined;
  }
};

export const ProductStep = ({ product, hideDescription }: ProductStepProps) => {
  const items = useRef<VariantQuantityMap>({});
  const [isValid, setIsValid] = useState(false);
  const { t } = useTranslation("checkout");
  // use this to force remount
  const [key, setKey] = useState(0);
  const publicBasket = usePublicBasket();

  const handleOnNext = useCallback(async () => {
    const returnedBasket = await addProductToBasket(
      items.current,
      publicBasket
    );

    // if we fail to modify the basket, then abort navigation by returning false
    if (!returnedBasket) {
      setKey(key + 1);
      return false;
    }

    return undefined;
  }, [publicBasket, key]);

  const handleOnChange = (c: VariantQuantityMap) => {
    items.current = c;
    const hasItems = hasValidCount(items.current);

    if (hasItems !== isValid) {
      setIsValid(hasItems);
    }
  };

  return (
    <PurchaseWizardModalStep
      stepId={PRODUCT_STEP_ID}
      stepName={t("Product")}
      isValid={isValid}
      onNext={handleOnNext}
      allowEmptyBasket
    >
      <ProductAddonsModalContent
        key={key}
        product={product}
        showCartTimer={false}
        onChange={handleOnChange}
        hideDescription={hideDescription}
      />
    </PurchaseWizardModalStep>
  );
};

ProductStep.id = PRODUCT_STEP_ID;
